import { ReactNode, createContext, useContext, useEffect, useState } from 'react';

import { Droppable } from '@hello-pangea/dnd';
import type { DroppableProvided, DroppableStateSnapshot } from '@hello-pangea/dnd';
import { ErrorOutline, InfoOutlined } from '@mui/icons-material';
import { Box, IconButton, SxProps, Theme, Tooltip, Typography } from '@mui/material';

import { useLanguage } from '../../hooks';
import { openFrontendRouteInNewTab, paths } from '../../utils/navigation';
import { SignatureOrderType, SignatureOrderUser, SignerData } from '../../types/taskForm';
import { DragAndDropSignerBox } from './DragAndDropSignerBox';
import { DragAndDropSignerGroupBox } from './DragAndDropSignerGroupBox';
import { generateGroupId, getDraggableId } from './utils';

// Lets QuorumStepper instances (one per group + the simultaneous one) report
// an invalid value up to FormSignatureBuilder, which blocks save and shows a
// warning, without prop-drilling through the order/group components.
export const QuorumValidationContext = createContext<{
  reportError: (id: string, invalid: boolean) => void;
  errorIds: string[];
} | null>(null);

interface SignatureOrderConfigProps {
  type: SignatureOrderType;
  isDragAndDropDisabled: boolean;
  isIncomplete?: boolean;
  order: SignatureOrderUser[][];
  usersData: Record<string, SignerData>;
  onSignerDelete: (signerId: string | number) => void;
  isCombineEnabled?: boolean;
  handleUnGroup?: (signerId: string | number, index: number) => void;
  sequentialInfo?: Record<string, { icon: JSX.Element; text: ReactNode }>;
  configState?: string;
  isQuorumEnabled?: boolean;
  quorumBlocked?: boolean;
  minimumSignatures?: (number | null)[];
  onMinimumSignaturesChange?: (minimumSignatures: (number | null)[]) => void;
}

const customStyles: Record<string, SxProps<Theme>> = {
  column: { width: '100%' },
  title: { fontSize: '20px', fontWeight: '500' },
  disabledTitle: {
    fontSize: '20px',
    fontWeight: '500',
    color: (theme) => theme.palette.text.disabled,
  },
  infoContainer: {
    display: 'flex',
    gap: '8px',
    alignItems: 'center',
    margin: '5px 0px 10px 0px',
  },
  infoText: { fontSize: '13px' },
  disabledInfoText: { color: (theme) => theme.palette.text.disabled, fontSize: '13px' },
  quorumErrorIcon: { color: (theme) => theme.palette.error.main, fontSize: '16px' },
  quorumErrorText: { fontSize: '13px', color: (theme) => theme.palette.error.main },
  container: {
    padding: '10px',
    borderRadius: '12px',
    height: '400px',
    overflowY: 'auto',
    border: '1px solid transparent',
    display: 'flex',
    flexDirection: 'column',
    gap: '10px',
  },
  draggingOverContainer: { border: (theme) => `1px dashed ${theme.palette.primary.main}` },
  emptyStateContainer: {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    justifyContent: 'center',
  },
  emptyStateText: {
    fontSize: '16px',
    textAlign: 'center',
    color: (theme) => theme.palette.text.disabled,
    maxWidth: '258px',
  },
  quorumContainer: {
    display: 'flex',
    alignItems: 'center',
    gap: '6px',
    padding: '0px',
    marginBottom: '4px',
    fontSize: '13px',
  },
  quorumButton: {
    width: '24px',
    height: '24px',
    padding: 0,
    color: (theme) => theme.palette.primary.main,
    '&:hover': {
      backgroundColor: 'transparent',
    },
    '&.Mui-disabled': {
      color: (theme) => theme.palette.text.disabled,
    },
  },
  quorumInput: {
    width: '32px',
    height: '24px',
    textAlign: 'center',
    fontSize: '14px',
    fontWeight: 500,
    color: (theme) => theme.palette.primary.main,
    border: '1px solid',
    borderColor: (theme) => theme.palette.grey[300],
    borderRadius: '4px',
    outline: 'none',
    fontFamily: 'inherit',
    '&:focus': {
      borderColor: (theme) => theme.palette.primary.main,
    },
  },
};

function EmptyDroppable({
  provided,
  snapshot,
  emptyText,
}: {
  provided: DroppableProvided;
  snapshot: DroppableStateSnapshot;
  emptyText: string;
}) {
  return (
    <Box
      ref={provided.innerRef}
      {...provided.droppableProps}
      sx={{ ...customStyles.container, ...customStyles.emptyStateContainer } as SxProps<Theme>}
    >
      <Box sx={{ display: 'none' }}>
        <span>{provided.placeholder}</span>
      </Box>
      <Typography
        sx={{
          ...customStyles.emptyStateText,
          color: (theme) =>
            snapshot.isDraggingOver ? theme.palette.primary.main : theme.palette.text.disabled,
        }}
      >
        {emptyText}
      </Typography>
    </Box>
  );
}

export function QuorumStepper({
  quorumId,
  groupSize,
  value,
  onChange,
  lang,
  tooltipKey = 'sequential',
  blocked = false,
}: {
  quorumId: string;
  groupSize: number;
  value: number | null;
  onChange?: (value: number | null) => void;
  lang: ReturnType<typeof useLanguage>;
  tooltipKey?: 'sequential' | 'simultaneous';
  blocked?: boolean;
}) {
  const current = value ?? groupSize;
  // Local editable string so the user can type freely; validation runs on blur/Enter.
  const [inputValue, setInputValue] = useState(String(current));
  const quorumValidation = useContext(QuorumValidationContext);

  // Reclamp the committed value when the group shrinks below it (e.g. a signer was removed):
  // otherwise a stale quorum > groupSize would be persisted to the backend. Clamp to null
  // ("all must sign") to keep a single convention with handleIncrement / commitInputValue,
  // which also use null for the "everyone" case instead of an explicit groupSize.
  useEffect(() => {
    if (value != null && value > groupSize) {
      onChange?.(null);
    }
  }, [value, groupSize, onChange]);

  // Keep the local input in sync when the committed value or group size changes.
  useEffect(() => {
    setInputValue(String(value ?? groupSize));
  }, [value, groupSize]);

  const parsed = inputValue === '' ? NaN : parseInt(inputValue, 10);
  const isInvalid =
    groupSize > 1 && (inputValue === '' || Number.isNaN(parsed) || parsed < 1 || parsed > groupSize);

  // Report invalid state up so save can be blocked and a warning shown. Depend on the
  // stable reportError callback (useCallback) rather than the whole quorumValidation object,
  // whose identity changes whenever errorIds changes and would otherwise re-run these effects
  // (including the cleanup) for every mounted stepper on each error toggle.
  const reportError = quorumValidation?.reportError;
  useEffect(() => {
    reportError?.(quorumId, isInvalid);
  }, [isInvalid, quorumId, reportError]);
  useEffect(() => {
    return () => reportError?.(quorumId, false);
  }, [quorumId, reportError]);

  if (groupSize <= 1) return null;

  const handleDecrement = () => {
    const next = current - 1;
    if (next < 1) return;
    onChange?.(next === groupSize ? null : next);
  };

  const handleIncrement = () => {
    const next = current + 1;
    if (next > groupSize) return;
    onChange?.(next === groupSize ? null : next);
  };

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    // Free editing while typing: digits only, empty allowed.
    setInputValue(e.target.value.replace(/\D/g, ''));
  };

  const commitInputValue = () => {
    if (inputValue === '') {
      // Empty → restore previous value.
      setInputValue(String(current));
      return;
    }
    let num = parseInt(inputValue, 10);
    if (Number.isNaN(num) || num < 1) num = 1;
    if (num > groupSize) num = groupSize;
    setInputValue(String(num));
    onChange?.(num === groupSize ? null : num);
  };

  const handleInputKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'Enter') {
      e.preventDefault();
      commitInputValue();
    }
  };

  const handleUpgradeClick = (e: React.MouseEvent) => {
    e.stopPropagation();
    openFrontendRouteInNewTab(paths.billing.index);
  };

  const stepper = (
    <Box
      sx={{
        ...customStyles.quorumContainer,
        ...(blocked && { opacity: 0.4, '& *': { pointerEvents: 'none' } }),
      }}
    >
      <Typography sx={{ fontSize: '14px', color: '#6E7178' }}>
        {lang.formSignatureBuilder.quorumLabel}
      </Typography>
      <IconButton
        size='small'
        onClick={handleDecrement}
        disabled={blocked || current <= 1}
        sx={customStyles.quorumButton}
      >
        <Typography sx={{ fontSize: '14px', fontWeight: 500, lineHeight: 1, color: 'inherit' }}>&minus;</Typography>
      </IconButton>
      <Box
        component='input'
        type='text'
        inputMode='numeric'
        value={inputValue}
        onChange={handleInputChange}
        onBlur={commitInputValue}
        onKeyDown={handleInputKeyDown}
        readOnly={blocked}
        sx={{
          ...customStyles.quorumInput,
          ...(isInvalid && {
            borderColor: (theme: Theme) => theme.palette.error.main,
            color: '#DB2E46',
            '&:focus': { borderColor: (theme: Theme) => theme.palette.error.main },
          }),
        }}
      />
      <IconButton
        size='small'
        onClick={handleIncrement}
        disabled={blocked || current >= groupSize}
        sx={customStyles.quorumButton}
      >
        <Typography sx={{ fontSize: '14px', fontWeight: 500, lineHeight: 1, color: 'inherit' }}>+</Typography>
      </IconButton>
      <Typography sx={{ fontSize: '14px', color: '#6E7178' }}>
        {lang.formSignatureBuilder.quorumSuffix}
      </Typography>
      {!blocked && (
        <Tooltip title={tooltipKey === 'simultaneous' ? lang.formSignatureBuilder.quorumTooltipSimultaneous : lang.formSignatureBuilder.quorumTooltip}>
          <InfoOutlined sx={{ fontSize: '16px', color: '#6E7178', cursor: 'pointer' }} />
        </Tooltip>
      )}
    </Box>
  );

  // Quorum bloqueado (plan sin firma avanzada): se muestra el control pero
  // deshabilitado, con el mismo mensaje y link de upgrade que el firmante dinamico.
  if (blocked) {
    const tooltipContent = (
      <Box sx={{ fontSize: '12px', lineHeight: '20px' }}>
        {lang.formSignatureBuilder.dynamicSignerTooltip
          .split(lang.formSignatureBuilder.upgradePlan)
          .map((part: string, index: number, array: string[]) => (
            <span key={index}>
              {part}
              {index < array.length - 1 && (
                <Box
                  component='span'
                  sx={{ color: '#D8EFFE', textDecoration: 'underline', cursor: 'pointer' }}
                  onClick={handleUpgradeClick}
                >
                  {lang.formSignatureBuilder.upgradePlan}
                </Box>
              )}
            </span>
          ))}
      </Box>
    );
    return (
      <Tooltip
        title={tooltipContent}
        placement='bottom'
        arrow
        slotProps={{ tooltip: { sx: { minWidth: '380px', backgroundColor: '#232426' } } }}
      >
        <Box sx={{ display: 'inline-flex', cursor: 'not-allowed' }}>{stepper}</Box>
      </Tooltip>
    );
  }

  return stepper;
}

export function SignatureOrderConfig({
  type,
  isDragAndDropDisabled,
  isIncomplete = false,
  order,
  usersData,
  onSignerDelete,
  isCombineEnabled = false,
  handleUnGroup,
  sequentialInfo,
  configState,
  isQuorumEnabled = false,
  quorumBlocked = false,
  minimumSignatures = [],
  onMinimumSignaturesChange,
}: SignatureOrderConfigProps) {
  const lang = useLanguage();
  const quorumValidation = useContext(QuorumValidationContext);
  const columnHasQuorumError =
    type === SignatureOrderType.SIMULTANEOUS
      ? quorumValidation?.errorIds.includes('simultaneous') ?? false
      : quorumValidation?.errorIds.some((id) => id.startsWith('sequential-')) ?? false;

  const headerTitle =
    type === SignatureOrderType.SIMULTANEOUS
      ? lang.formSignatureBuilder.simultaneousSign
      : lang.formSignatureBuilder.sequentialSign;

  const headerIcon =
    type === SignatureOrderType.SIMULTANEOUS ? (
      <InfoOutlined sx={{ color: (theme) => theme.palette.primary.main, fontSize: '16px' }} />
    ) : configState && sequentialInfo ? (
      sequentialInfo[configState]?.icon ?? <InfoOutlined sx={{ fontSize: '16px' }} />
    ) : (
      <InfoOutlined sx={{ fontSize: '16px' }} />
    );

  const headerText =
    type === SignatureOrderType.SIMULTANEOUS
      ? lang.formSignatureBuilder.simultaneousSignInfo
      : configState && sequentialInfo
      ? sequentialInfo[configState]?.text
      : '';

  const emptyText =
    type === SignatureOrderType.SIMULTANEOUS
      ? lang.formSignatureBuilder.simultaneousEmptyState
      : isDragAndDropDisabled
      ? lang.formSignatureBuilder.optionalSignatureOrderDisabled
      : lang.formSignatureBuilder.sequentialEmptyState;

  const handleQuorumChange = (index: number, value: number | null) => {
    if (!onMinimumSignaturesChange) return;
    const updated = [...minimumSignatures];
    while (updated.length <= index) updated.push(null);
    updated[index] = value;
    onMinimumSignaturesChange(updated);
  };

  const totalSimultaneousSigners = order.reduce((sum, group) => sum + group.length, 0);

  return (
    <Box sx={customStyles.column}>
      <Typography sx={isDragAndDropDisabled ? customStyles.disabledTitle : customStyles.title}>
        {headerTitle}
      </Typography>
      <Box sx={customStyles.infoContainer}>
        {columnHasQuorumError ? (
          <>
            <ErrorOutline sx={customStyles.quorumErrorIcon} />
            <Typography sx={customStyles.quorumErrorText}>
              {lang.formSignatureBuilder.quorumError}
            </Typography>
          </>
        ) : (
          <>
            {headerIcon}
            <Typography
              sx={isDragAndDropDisabled ? customStyles.disabledInfoText : customStyles.infoText}
            >
              {typeof headerText === 'string' ? headerText : <>{headerText}</>}
            </Typography>
          </>
        )}
      </Box>
      <Droppable
        droppableId={type}
        isDropDisabled={isDragAndDropDisabled}
        isCombineEnabled={type === SignatureOrderType.SEQUENTIAL ? isCombineEnabled : false}
      >
        {(provided, snapshot) => {
          const containerSx = {
            ...customStyles.container,
            ...(snapshot.isDraggingOver ? customStyles.draggingOverContainer : {}),
          } as SxProps<Theme>;

          if (order.length === 0) {
            return (
              <EmptyDroppable
                provided={provided}
                snapshot={snapshot}
                emptyText={emptyText}
              />
            );
          }

          if (type === SignatureOrderType.SIMULTANEOUS) {
            return (
              <Box
                ref={provided.innerRef}
                {...provided.droppableProps}
                sx={containerSx}
              >
                <Box sx={{ display: 'none' }}>
                  <span>{provided.placeholder}</span>
                </Box>
                {(isQuorumEnabled || quorumBlocked) && totalSimultaneousSigners > 1 && (
                  <QuorumStepper
                    quorumId='simultaneous'
                    groupSize={totalSimultaneousSigners}
                    value={minimumSignatures[0] ?? null}
                    onChange={(value) => handleQuorumChange(0, value)}
                    lang={lang}
                    tooltipKey='simultaneous'
                    blocked={quorumBlocked}
                  />
                )}
                {order.map((signerGroup, index) => (
                  <DragAndDropSignerBox
                    key={getDraggableId(signerGroup[0])}
                    signer={signerGroup}
                    index={index}
                    usersData={usersData}
                    onSignerDelete={onSignerDelete}
                    orderType={SignatureOrderType.SIMULTANEOUS}
                    isDragDisabled={isDragAndDropDisabled}
                    isIncomplete={isIncomplete}
                  />
                ))}
              </Box>
            );
          }

          return (
            <Box
              ref={provided.innerRef}
              {...provided.droppableProps}
              sx={containerSx}
            >
              <Box sx={{ display: 'none' }}>
                <span>{provided.placeholder}</span>
              </Box>
              {order.map((signers, index) => (
                <DragAndDropSignerGroupBox
                  key={generateGroupId(signers)}
                  signers={signers}
                  index={index}
                  isDragDisabled={isDragAndDropDisabled}
                  usersData={usersData}
                  onSignerDelete={onSignerDelete}
                  handleUnGroup={handleUnGroup}
                  isQuorumEnabled={isQuorumEnabled}
                  quorumBlocked={quorumBlocked}
                  quorumId={`sequential-${index}`}
                  quorumValue={minimumSignatures[index] ?? null}
                  onQuorumChange={(value) => handleQuorumChange(index, value)}
                />
              ))}
            </Box>
          );
        }}
      </Droppable>
    </Box>
  );
}
